
import { ChatMessage, VehicleData } from '@/types';
import { URI } from '@/types/enum/api.enum';
import { PartsCatalogResponse, PCDBPart, VMRSPart, FEType, FEResponse, FESearchResponse } from '@/types/parts';
import { apiInterceptor } from './ApiInterceptor';
import { log } from 'console';

interface ChatResponse {
  success: boolean;
  message?: ChatMessage;
  error?: string;
}

type ChatRequest = {
  message: string;
  image?: File;
  [key: string]: unknown;
}

interface PartsSearchResponse {
  success: boolean;
  parts?: PCDBPart[];
  vmrsParts?: VMRSPart[];
  pt?: string;
  markdownContent?: string;
  responseType?: 'json' | 'markdown';
  error?: string;
}

class ChatService {
  private static instance: ChatService;
  private readonly STORAGE_KEY = 'chat_history';

  private constructor() { }

  public static getInstance(): ChatService {
    if (!ChatService.instance) {
      ChatService.instance = new ChatService();
    }
    return ChatService.instance;
  }

  public async searchParts(vehicleData: VehicleData, query: string): Promise<PartsSearchResponse> {
    try {
      const response = await apiInterceptor.post<PartsCatalogResponse, { text: string, vmrs:boolean }>(
        URI.SEARCH,
        { text: query, vmrs: true },
        {
          timeout: 40000,
          headers: {
            'X-Request-Type': 'parts-search',
            'X-Vehicle-Data': JSON.stringify(vehicleData)
          }
        }
      );

      if (response.type === 'markdown' || response.type === 'html') {
        return {
          success: true,
          markdownContent: response.text,
          responseType: 'markdown'
        };
      }

      return {
        success: true,
        parts: response.data?.parts_catalog_pcdb.parts.pcdb,
        vmrsParts: response.data?.parts_catalog_pcdb.parts.vmrs,
        pt: response.data?.parts_catalog_pcdb.pt,
        responseType: 'json'
      };
    } catch (error) {
      console.error('Error searching parts:', error);
      return {
        success: false,
        error: 'Failed to search for parts. Please try again.',
      };
    }
  }

  public async searchFeatures(query: string, types: FEType[]): Promise<FESearchResponse> {
    try {
      const response = await apiInterceptor.post<FEResponse, { text: string, type: FEType[] }>(
        URI.FEATURE_EXTRACTION,
        { text: query, type: types },
        {
          timeout: 40000,
          headers: {
            'X-Request-Type': 'feature-extraction'
          }
        }
      );

      return {
        success: true,
        data: response
      };
    } catch (error) {
      console.error('Error extracting features:', error);
      return {
        success: false,
        error: 'Failed to extract features. Please try again.',
      };
    }
  }

  public generateChatResponse(
    pcdbParts: PCDBPart[] = [], 
    vmrsParts: VMRSPart[] = [], 
    query: string, 
    pt?: string,
    markdownContent?: string,
    responseType: 'json' | 'markdown' = 'json',
    mode?: 'agentic' | 'fe',
    feResponse?: FEResponse
  ): ChatMessage {
    if (responseType === 'markdown' && markdownContent) {
      return {
        id: Date.now().toString(),
        type: 'assistant',
        content: '', // Empty content for markdown responses
        timestamp: new Date(),
        responseType: 'markdown',
        markdownContent,
        mode: mode || 'agentic'
      };
    }

    if (mode === 'fe' && feResponse) {
      const totalComponents = feResponse.components?.length || 0;
      const totalRepairs = feResponse.repairs?.length || 0;
      const totalJobs = feResponse.repair_jobs?.length || 0;
      const totalItems = totalComponents + totalRepairs + totalJobs;

      let content = '';
      if (totalItems === 0) {
        content = 'I couldn\'t find any features matching your search. Please try different search terms.';
      } else {
        const parts = [];
        if (totalComponents > 0) parts.push(`${totalComponents} component${totalComponents > 1 ? 's' : ''}`);
        if (totalRepairs > 0) parts.push(`${totalRepairs} repair${totalRepairs > 1 ? 's' : ''}`);
        if (totalJobs > 0) parts.push(`${totalJobs} repair job${totalJobs > 1 ? 's' : ''}`);
        content = `Found ${parts.join(', ')} for "${query}":`;
      }

      return {
        id: Date.now().toString(),
        type: 'assistant',
        content,
        timestamp: new Date(),
        mode: 'fe',
        feResponse
      };
    }

    const totalParts = (pcdbParts?.length || 0) + (vmrsParts?.length || 0);

    let content = '';
    if (totalParts === 0) {
      content = 'I couldn\'t find any parts matching your search. Please try different search terms.';
    } else {
      const searchTerm = pt || query;
      content = `Looks like you're searching for ${searchTerm}. Here are the matching parts I found:`;
    }

    return {
      id: Date.now().toString(),
      type: 'assistant',
      content,
      timestamp: new Date(),
      pcdbParts,
      vmrsParts,
      responseType: 'json',
      mode: mode || 'agentic'
    };
  }

  public async sendMessage(message: string, image?: File): Promise<ChatResponse> {
    try {
      const formData = new FormData();
      formData.append('message', message);
      if (image) {
        formData.append('image', image);
      }

      const response = await apiInterceptor.post<ChatResponse, ChatRequest>(
        URI.SEARCH,
        { message, image },
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-Request-Type': 'chat'
          },
          timeout: 30000 // 30 second timeout for chat
        }
      );

      if (response.success && response.message) {
        this.saveMessage(response.message);
      }

      return response;
    } catch (error) {
      console.error('Chat error:', error);
      return {
        success: false,
        error: 'Failed to send message. Please try again.'
      };
    }
  }

  public async getChatHistory(): Promise<ChatMessage[]> {
    try {
      const response = await apiInterceptor.get<ChatMessage[]>(
        URI.CITATIONS,
        {
          timeout: 5000, // 5 second timeout for history
          headers: {
            'X-Request-Type': 'chat-history'
          }
        }
      );

      if (response) {
        this.saveChatHistory(response);
      }

      return response || [];
    } catch (error) {
      console.error('Chat history error:', error);
      // Return cached history if available
      return this.getCachedChatHistory();
    }
  }

  public async clearChatHistory(): Promise<void> {
    try {
      await apiInterceptor.delete(URI.CITATIONS, {
        timeout: 3000, // 3 second timeout for clear
        headers: {
          'X-Request-Type': 'clear-chat'
        }
      });
      this.clearCachedHistory();
    } catch (error) {
      console.error('Clear chat history error:', error);
      // Still clear local cache even if API fails
      this.clearCachedHistory();
    }
  }

  private saveMessage(message: ChatMessage): void {
    if (typeof window === 'undefined') return;

    const history = this.getCachedChatHistory();
    history.push(message);
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(history));
  }

  private saveChatHistory(history: ChatMessage[]): void {
    if (typeof window === 'undefined') return;
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(history));
  }

  private getCachedChatHistory(): ChatMessage[] {
    if (typeof window === 'undefined') return [];
    const data = localStorage.getItem(this.STORAGE_KEY);
    return data ? JSON.parse(data) : [];
  }

  private clearCachedHistory(): void {
    if (typeof window === 'undefined') return;
    localStorage.removeItem(this.STORAGE_KEY);
  }
}

export const chatService = ChatService.getInstance();
